import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  calcularDesdeNeto,
  calcularDesdeTotal,
  formatearCLP,
  formatearPorcentajeSimple,
  type ModalidadMonto,
} from '@lexicore/shared';
import { IconoAlerta, IconoCerebro, IconoIzquierda, IconoRayo } from '../componentes/Iconos';
import { Cargando, Panel, Vacio } from '../componentes/UI';
import { CurvaProbabilidad } from '../componentes/CurvaProbabilidad';
import { useAvisos } from '../contextos/AvisosContext';
import { useCategorias, useOrganismos, usePrediccion, type EntradaPrediccion } from '../lib/consultas';
import { useCrearLicitacionRapida } from '../lib/mutaciones';

/**
 * Formulario de oferta con el asistente de precio.
 *
 * La predicción se pide mientras se escribe, igual que el `triggerPredictor` del
 * CRM anterior — pero ahora contra el servidor, que ve todo el histórico, y con
 * el mismo motor que después deja constancia de lo recomendado.
 */
export function NuevaOferta() {
  const navegar = useNavigate();
  const [parametros] = useSearchParams();
  const categorias = useCategorias();
  const organismos = useOrganismos();

  const avisos = useAvisos();
  const mutacionCrear = useCrearLicitacionRapida();

  const [categoriaId, setCategoriaId] = useState('');
  const [organismoId, setOrganismoId] = useState(() => parametros.get('organismoId') ?? '');
  const [modalidadMonto, setModalidadMonto] = useState<ModalidadMonto>('NETO');
  const [presupuestoNeto, setPresupuestoNeto] = useState(0);
  const [presupuestoBruto, setPresupuestoBruto] = useState(0);
  const [plazoMaximo, setPlazoMaximo] = useState(0);
  const [oferentes, setOferentes] = useState<number | ''>('');
  const [precioElegido, setPrecioElegido] = useState<number | null>(null);

  const [codigoMercadoPublico, setCodigoMercadoPublico] = useState('');
  const [nombreLicitacion, setNombreLicitacion] = useState('');

  const handleCambioPresupuestoNeto = (valor: number) => {
    setPresupuestoNeto(valor);
    setPrecioElegido(null);
    if (valor > 0) {
      const cal = calcularDesdeNeto(valor, 19);
      setPresupuestoBruto(cal.montoTotal);
    } else {
      setPresupuestoBruto(0);
    }
  };

  const handleCambioPresupuestoBruto = (valor: number) => {
    setPresupuestoBruto(valor);
    setPrecioElegido(null);
    if (valor > 0) {
      const cal = calcularDesdeTotal(valor, 19);
      setPresupuestoNeto(cal.montoNeto);
    } else {
      setPresupuestoNeto(0);
    }
  };

  const entrada = useMemo<EntradaPrediccion | null>(() => {
    if (!categoriaId || presupuestoNeto <= 0) return null;
    return {
      categoriaId,
      presupuestoOficialNeto: presupuestoNeto,
      presupuestoOficialBruto: presupuestoBruto > 0 ? presupuestoBruto : undefined,
      modalidadMonto,
      organismoId: organismoId || null,
      plazoMaximoBasesDias: plazoMaximo || null,
      cantidadOferentesEsperada: oferentes === '' ? null : Number(oferentes),
    };
  }, [categoriaId, presupuestoNeto, presupuestoBruto, modalidadMonto, organismoId, plazoMaximo, oferentes]);

  const prediccion = usePrediccion(entrada);
  const categoria = categorias.data?.find((c) => c.id === categoriaId);

  const guardarEnPipeline = () => {
    if (!codigoMercadoPublico.trim()) {
      avisos.error('Indica el código de Mercado Público (ej: 1234-56-LP26)');
      return;
    }
    if (!nombreLicitacion.trim()) {
      avisos.error('Indica el nombre de la licitación');
      return;
    }
    if (!categoriaId) {
      avisos.error('Selecciona una categoría');
      return;
    }

    const orgSeleccionado = organismos.data?.find((o) => o.id === organismoId);
    const precioFinalNeto = precioElegido ?? prediccion.data?.precio.precioSugeridoNeto ?? prediccion.data?.precio.precioSugerido ?? presupuestoNeto;
    const desglose = calcularDesdeNeto(precioFinalNeto, 19);
    const plazoDias = prediccion.data?.plazo.plazoSugeridoDias ?? plazoMaximo ?? 30;

    mutacionCrear.mutate(
      {
        codigoMercadoPublico: codigoMercadoPublico.trim(),
        nombre: nombreLicitacion.trim(),
        nombreOrganismo: orgSeleccionado ? orgSeleccionado.nombre : 'Organismo Público',
        organismoId: orgSeleccionado ? orgSeleccionado.id : undefined,
        categoriaId,
        tipoProceso: 'LICITACION',
        estado: 'OPORTUNIDAD',
        prioridad: 'MEDIA',
        modalidadMonto,
        presupuestoOficialNeto: presupuestoNeto,
        presupuestoOficialBruto: presupuestoBruto || desglose.montoTotal,
        montoOfertadoNeto: precioFinalNeto,
        montoOfertadoBruto: desglose.montoTotal,
        plazoEntregaOfertadoDias: plazoDias,
        plazoMaximoBasesDias: plazoMaximo || plazoDias,
        duracionMeses: 12,
      },
      {
        onSuccess: () => {
          avisos.exito('¡Oferta guardada exitosamente en el Pipeline!');
          navegar('/pipeline');
        },
        onError: (err: any) => {
          avisos.error(err?.message || 'Error al guardar la oferta');
        },
      },
    );
  };

  return (
    <>
      <button type="button" className="btn btn-secondary btn-sm" onClick={() => navegar(-1)} style={{ marginBottom: 18 }}>
        <IconoIzquierda size={13} />
        Volver
      </button>

      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 6, letterSpacing: '-0.01em' }}>Nueva oferta</h1>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          Completa el presupuesto oficial y la categoría, y el motor estima el precio con el que compite el mercado.
        </p>
      </div>

      <Panel style={{ marginBottom: 20 }}>
        <div className="form-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
          <div className="form-group">
            <label htmlFor="categoria">Categoría</label>
            <select id="categoria" value={categoriaId} onChange={(e) => { setCategoriaId(e.target.value); setPrecioElegido(null); }}>
              <option value="">Selecciona…</option>
              {categorias.data?.map((c) => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </select>
            {categoria && (
              <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                Margen objetivo: {categoria.margenObjetivoPct}%
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="organismo">Organismo</label>
            <select id="organismo" value={organismoId} onChange={(e) => { setOrganismoId(e.target.value); setPrecioElegido(null); }}>
              <option value="">Cualquiera</option>
              {organismos.data?.map((o) => (
                <option key={o.id} value={o.id}>{o.nombre}</option>
              ))}
            </select>
            <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
              Indicarlo pondera más su historial
            </span>
          </div>

          <div className="form-group">
            <label htmlFor="modalidad">Modalidad de Monto</label>
            <select id="modalidad" value={modalidadMonto} onChange={(e) => setModalidadMonto(e.target.value as ModalidadMonto)}>
              <option value="NETO">Neto (sin IVA)</option>
              <option value="BRUTO">Bruto (con IVA 19%)</option>
            </select>
            <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
              Afecta cómo se expresan los precios
            </span>
          </div>

          <div className="form-group">
            <label htmlFor="presupuesto-neto">Presupuesto oficial neto (CLP)</label>
            <input
              id="presupuesto-neto"
              type="number"
              value={presupuestoNeto || ''}
              onChange={(e) => handleCambioPresupuestoNeto(Number(e.target.value))}
              min={0}
              placeholder="0"
            />
            <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
              Sin IVA: {formatearCLP(presupuestoNeto)}
            </span>
          </div>

          <div className="form-group">
            <label htmlFor="presupuesto-bruto">Presupuesto oficial bruto (con IVA)</label>
            <input
              id="presupuesto-bruto"
              type="number"
              value={presupuestoBruto || ''}
              onChange={(e) => handleCambioPresupuestoBruto(Number(e.target.value))}
              min={0}
              placeholder="0"
            />
            <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
              Total con 19% IVA: {formatearCLP(presupuestoBruto)}
            </span>
          </div>

          <div className="form-group">
            <label htmlFor="plazo">Plazo máximo de las bases (días)</label>
            <input id="plazo" type="number" value={plazoMaximo || ''} onChange={(e) => setPlazoMaximo(Number(e.target.value))} min={0} placeholder="Opcional" />
          </div>

          <div className="form-group">
            <label htmlFor="oferentes">Oferentes esperados</label>
            <input
              id="oferentes"
              type="number"
              value={oferentes}
              onChange={(e) => setOferentes(e.target.value === '' ? '' : Number(e.target.value))}
              min={0}
              placeholder="Opcional"
            />
          </div>
        </div>
      </Panel>

      {!entrada && (
        <Panel>
          <Vacio
            titulo="Falta el presupuesto oficial"
            detalle="Sin él no existe el ratio precio/presupuesto sobre el que se construye toda la estimación. Es el dato que más rinde completar."
          />
        </Panel>
      )}

      {entrada && prediccion.isLoading && <Cargando texto="Consultando el histórico…" />}

      {entrada && prediccion.isError && (
        <Panel>
          <Vacio
            titulo="No se pudo calcular la recomendación"
            detalle={prediccion.error instanceof Error ? prediccion.error.message : undefined}
          />
        </Panel>
      )}

      {prediccion.data && (
        <div
          style={{
            background: 'var(--accent-blue-soft)',
            border: '1px solid var(--accent-blue)',
            borderRadius: 'var(--radius-sm)',
            padding: '20px 22px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, gap: 16, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <IconoCerebro size={22} style={{ color: 'var(--accent-blue)' }} />
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>Asistente predictivo de precio y plazo</div>
                <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 2, fontFamily: 'var(--font-mono)' }}>
                  modelo {prediccion.data.modeloVersion}
                </div>
              </div>
            </div>

            <span
              className="factura-badge"
              style={{
                background: nivelFondo(prediccion.data.precio.evidencia.nivel),
                color: nivelColor(prediccion.data.precio.evidencia.nivel),
                fontSize: 10,
              }}
            >
              CONFIANZA {prediccion.data.precio.evidencia.nivel} · n efectivo{' '}
              {prediccion.data.precio.evidencia.nEfectivo.toFixed(2)}
            </span>
          </div>

          {prediccion.data.precio.precioSugerido === 0 ? (
            <Vacio
              titulo="Todavía no hay historial suficiente en esta categoría"
              detalle={prediccion.data.precio.advertencias[0]}
            />
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 14, marginBottom: 20 }}>
                <CeldaResumen
                  etiqueta="Precio sugerido"
                  valor={`${formatearCLP(prediccion.data.precio.precioSugeridoNeto ?? prediccion.data.precio.precioSugerido)} Neto`}
                  color="var(--accent-blue)"
                  nota={`Con IVA: ${formatearCLP(prediccion.data.precio.precioSugeridoBruto ?? Math.round(prediccion.data.precio.precioSugerido * 1.19))} | Rango Neto ${formatearCLP(prediccion.data.precio.rangoMinNeto ?? prediccion.data.precio.rangoMin)} – ${formatearCLP(prediccion.data.precio.rangoMaxNeto ?? prediccion.data.precio.rangoMax)}`}
                />
                <CeldaResumen
                  etiqueta="Precio del mercado"
                  valor={formatearPorcentajeSimple(prediccion.data.precio.ratioMercado * 100, 1)}
                  nota="del presupuesto oficial"
                />
                <CeldaResumen
                  etiqueta="Piso por margen"
                  valor={
                    (prediccion.data.precio.pisoPorMargenNeto ?? prediccion.data.precio.pisoPorMargen) !== null
                      ? `${formatearCLP(prediccion.data.precio.pisoPorMargenNeto ?? prediccion.data.precio.pisoPorMargen!)} Neto`
                      : '—'
                  }
                  color="var(--accent-red)"
                  nota={
                    (prediccion.data.precio.pisoPorMargenNeto ?? prediccion.data.precio.pisoPorMargen) === null
                      ? 'sin datos de costo'
                      : `Con IVA: ${formatearCLP(prediccion.data.precio.pisoPorMargenBruto ?? Math.round((prediccion.data.precio.pisoPorMargen ?? 0) * 1.19))}`
                  }
                />
                <CeldaResumen
                  etiqueta="Plazo sugerido"
                  valor={prediccion.data.plazo.plazoSugeridoDias === null ? '—' : `${prediccion.data.plazo.plazoSugeridoDias} días`}
                  nota={
                    prediccion.data.plazo.limitadoPorBases
                      ? 'ajustado al tope de las bases'
                      : plazoMaximo > 0
                        ? `tope de bases: ${plazoMaximo} días`
                        : 'sin tope indicado'
                  }
                />
              </div>

              {prediccion.data.precio.curvaProbabilidad.length > 0 && (
                <CurvaProbabilidad
                  curva={prediccion.data.precio.curvaProbabilidad}
                  pisoPorMargen={prediccion.data.precio.pisoPorMargen}
                  precioSugerido={prediccion.data.precio.precioSugerido}
                  precioElegido={precioElegido}
                  alElegir={setPrecioElegido}
                  modalidadMonto={modalidadMonto}
                />
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.55, padding: '11px 14px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)' }}>
                  {prediccion.data.precio.evidencia.detalle}
                </div>

                {prediccion.data.precio.advertencias.map((advertencia, indice) => (
                  <div
                    key={indice}
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'flex-start',
                      padding: '11px 14px',
                      background: 'var(--accent-yellow-soft)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 11.5,
                      lineHeight: 1.55,
                      color: 'var(--text-secondary)',
                    }}
                  >
                    <IconoAlerta size={15} style={{ color: 'var(--accent-yellow)', flexShrink: 0, marginTop: 1 }} />
                    <span>{advertencia}</span>
                  </div>
                ))}

                {prediccion.data.plazo.advertencias.map((advertencia, indice) => (
                  <div
                    key={`plazo-${indice}`}
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'flex-start',
                      padding: '11px 14px',
                      background: 'var(--accent-blue-soft)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 11.5,
                      lineHeight: 1.55,
                      color: 'var(--text-secondary)',
                    }}
                  >
                    <IconoAlerta size={15} style={{ color: 'var(--accent-blue)', flexShrink: 0, marginTop: 1 }} />
                    <span>{advertencia}</span>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 20, paddingTop: 18, borderTop: '1px solid rgba(59, 130, 246, 0.2)' }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 7, color: 'var(--text-primary)' }}>
                  <IconoRayo size={16} style={{ color: 'var(--accent-blue)' }} />
                  <span>Guardar esta oferta estimada directamente en el Pipeline</span>
                </div>

                <div className="form-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', marginBottom: 14 }}>
                  <div className="form-group">
                    <label htmlFor="codigo-mp">Código Mercado Público *</label>
                    <input
                      id="codigo-mp"
                      type="text"
                      placeholder="Ej: 1234-56-LP26"
                      value={codigoMercadoPublico}
                      onChange={(e) => setCodigoMercadoPublico(e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <label htmlFor="nombre-licitacion">Nombre de la Licitación / Servicio *</label>
                    <input
                      id="nombre-licitacion"
                      type="text"
                      placeholder="Ej: Servicio de Mantenimiento de Servidores"
                      value={nombreLicitacion}
                      onChange={(e) => setNombreLicitacion(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  {(() => {
                    const precioNetoElegido = precioElegido ?? prediccion.data.precio.precioSugeridoNeto ?? prediccion.data.precio.precioSugerido;
                    const precioBrutoElegido = calcularDesdeNeto(precioNetoElegido, 19).montoTotal;
                    return (
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={mutacionCrear.isPending}
                        onClick={guardarEnPipeline}
                        style={{ gap: 7, padding: '9px 18px' }}
                      >
                        <IconoRayo size={15} />
                        <span>
                          {mutacionCrear.isPending
                            ? 'Guardando en Pipeline…'
                            : `⚡ Guardar en Pipeline: ${formatearCLP(precioNetoElegido)} Neto (${formatearCLP(precioBrutoElegido)} con IVA)`}
                        </span>
                      </button>
                    );
                  })()}
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    Se creará en la columna &ldquo;Oportunidad&rdquo; con el plazo sugerido (
                    {prediccion.data.plazo.plazoSugeridoDias ?? plazoMaximo ?? 30} días).
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

function CeldaResumen({ etiqueta, valor, color, nota }: { etiqueta: string; valor: string; color?: string; nota?: string }) {
  return (
    <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', padding: '12px 14px' }}>
      <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>{etiqueta}</div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 17, fontWeight: 700, marginTop: 5, color: color ?? 'var(--text-primary)' }}>
        {valor}
      </div>
      {nota && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>{nota}</div>}
    </div>
  );
}

function nivelColor(nivel: string): string {
  if (nivel === 'ALTA') return 'var(--accent-green)';
  if (nivel === 'MEDIA') return 'var(--accent-yellow)';
  return 'var(--accent-red)';
}

function nivelFondo(nivel: string): string {
  if (nivel === 'ALTA') return 'var(--accent-green-soft)';
  if (nivel === 'MEDIA') return 'var(--accent-yellow-soft)';
  return 'var(--accent-red-soft)';
}
